import { Link } from 'react-router-dom'
import {ArrowLeft} from 'lucide-react'
import { useLang } from '@/context/LanguageContext'
import Seo from '@/components/Seo'

export default function TermsOfSale() {
  const { t } = useLang()

  return (
    <div style={{ backgroundColor: '#F8F2EB', minHeight: '100vh' }}>
      <Seo
        title="Conditions générales de vente — Clips'On"
        description="Conditions de commande, prix, livraison, droit de rétractation et garanties applicables aux achats effectués sur clips-on.ch."
        path="/terms-of-sale"
      />
      {/* Header/Navigation */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 transition-colors"
          style={{ color: '#82391F', fontSize: '15px' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#E1593F')}
          onMouseLeave={e => (e.currentTarget.style.color = '#82391F')}
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          {t("Retour à l'accueil", "Back to home")}
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <h1 
          className="mb-4 tracking-[0.25em] uppercase"
          style={{ 
            color: '#0D0C0B',
            fontSize: '32px',
            fontWeight: 300,
            letterSpacing: '4px'
          }}
        >
          {t("Conditions Générales de Vente", "General Terms and Conditions of Sale")}
        </h1>
        
        <p className="mb-16" style={{ color: '#82391F', fontSize: '15px', opacity: 0.6 }}>
          {t("Dernière mise à jour : mars 2026", "Last updated: March 2026")}
        </p>

        <div className="space-y-12">
          {/* Article 1 — Objet */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 1 — Objet", "Article 1 — Purpose")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Les présentes Conditions Générales de Vente (CGV) régissent les ventes de produits Clips'ON effectuées sur le site clips-on.ch, édité par Clips'ON Frédéric Hänni, entreprise individuelle, sise Avenue de Champel 28, 1206 Genève, Suisse, IDE : CHE-273.712.623, inscrite au Registre du commerce de Genève le 7 juillet 2026.",
                  "These General Terms and Conditions of Sale govern all purchases of Clips'ON products made on clips-on.ch, published by Clips'ON Frédéric Hänni, a sole proprietorship, located at Avenue de Champel 28, 1206 Geneva, Switzerland, Business identification number (UID): CHE-273.712.623, registered with the Geneva Commercial Registry on 7 July 2026."
                )}
              </p>
              <p>
                {t(
                  "Toute commande passée sur le site implique l'acceptation pleine et entière des présentes CGV.",
                  "Placing an order on the website implies full acceptance of these terms."
                )}
              </p>
            </div>
          </section>

          {/* Article 2 — Produits */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 2 — Produits", "Article 2 — Products")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Clips'ON est un bouton vestimentaire sans couture en Nylon 6/6, disponible en 28 couleurs et en deux tailles (18 mm et 20 mm). Il est composé de trois pièces : un pic, une embase et une capsule de couleur interchangeable.",
                  "Clips'ON is a no-sew clothing button made of Nylon 6/6, available in 28 colours and two sizes (18 mm and 20 mm). It consists of three parts: a stud, a base, and an interchangeable colour cap."
                )}
              </p>
              <p className="mb-3">
                {t(
                  "Les produits sont vendus en pochettes de 5 boutons au prix de 4,50 € la pochette. Le nombre maximum de pochettes par commande est de 3 (toutes couleurs confondues).",
                  "Products are sold in pouches of 5 buttons at €4.50 per pouch. The maximum number of pouches per order is 3 (all colours combined)."
                )}
              </p>
              <p>
                {t(
                  "Les photographies des produits sont aussi fidèles que possible, mais de légères variations de couleur peuvent exister entre l'écran et le produit réel.",
                  "Product photographs are as accurate as possible, but slight colour variations may exist between the screen display and the actual product."
                )}
              </p>
            </div>
          </section>

          {/* Article 3 — Avertissement de sécurité */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 3 — Avertissement de sécurité", "Article 3 — Safety warning")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p>
                {t(
                  "Clips'ON ne convient pas aux enfants de moins de 36 mois. Le produit contient des petites pièces pointues — risque d'étouffement et de blessure. Tenir hors de portée des jeunes enfants.",
                  "Clips'ON is not suitable for children under 36 months. The product contains small sharp parts \u2014 choking and injury hazard. Keep out of reach of young children."
                )}
              </p>
            </div>
          </section>

          {/* Article 4 — Commande */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 4 — Commande", "Article 4 — Ordering")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Le client passe commande en ajoutant des produits à son panier, puis en renseignant ses coordonnées de livraison et en procédant au paiement. Un email de confirmation est envoyé à l'adresse indiquée.",
                  "Customers place orders by adding products to their cart, entering their delivery details, and completing payment. A confirmation email is sent to the address provided."
                )}
              </p>
              <p>
                {t(
                  "Clips'ON se réserve le droit de refuser toute commande en cas de motif légitime (informations erronées, commande anormale, etc.).",
                  "Clips'ON reserves the right to refuse any order for legitimate reasons (incorrect information, unusual orders, etc.)."
                )}
              </p>
            </div>
          </section>

          {/* Article 5 — Prix et paiement */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 5 — Prix et paiement", "Article 5 — Prices and payment")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Les prix sont indiqués en euros (€), toutes taxes comprises. Clips'ON n'est pas assujetti à la TVA (en dessous du seuil d'assujettissement suisse de CHF 100 000).",
                  "Prices are listed in euros (€), all taxes included. Clips'ON is not subject to VAT (below the Swiss threshold of CHF 100,000)."
                )}
              </p>
              <p>
                {t(
                  "Le paiement est effectué en ligne par carte bancaire via la plateforme sécurisée Stripe. Le montant est débité au moment de la validation de la commande.",
                  "Payment is made online by credit card through the secure Stripe platform. The amount is charged when the order is confirmed."
                )}
              </p>
            </div>
          </section>

          {/* Article 6 — Livraison */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 6 — Livraison", "Article 6 — Delivery")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Les commandes sont expédiées par lettre suivie depuis la France. Les frais de livraison sont à la charge du client et calculés en fonction du pays de destination :",
                  "Orders are shipped by tracked mail from France. Shipping costs are borne by the customer and calculated based on the destination country:"
                )}
              </p>
              <ul className="mb-3 ml-6 space-y-1" style={{ listStyleType: 'disc' }}>
                <li>{t("France : 2,90 € (1 pochette) / 3,90 € (2 ou 3 pochettes)", "France: €2.90 (1 pouch) / €3.90 (2 or 3 pouches)")}</li>
                <li>{t("International (tous les autres pays) : 5,90 € (1 pochette) / 8,50 € (2 ou 3 pochettes)", "International (all other countries): €5.90 (1 pouch) / €8.50 (2 or 3 pouches)")}</li>
              </ul>
              <p>
                {t(
                  "Les délais de livraison indicatifs sont de 3 à 5 jours ouvrés pour la France, 5 à 10 jours ouvrés pour l'Europe, et 10 à 20 jours ouvrés pour les destinations internationales. Ces délais ne constituent pas un engagement contractuel.",
                  "Estimated delivery times are 3 to 5 working days for France, 5 to 10 working days for Europe, and 10 to 20 working days for international destinations. These timeframes are indicative and do not constitute a contractual commitment."
                )}
              </p>
            </div>
          </section>

          {/* Article 7 — Droit de rétractation */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 7 — Droit de rétractation", "Article 7 — Right of withdrawal")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Conformément à la législation européenne sur la vente à distance, le client dispose d'un délai de 14 jours à compter de la réception de sa commande pour exercer son droit de rétractation, sans avoir à justifier de motif.",
                  "In accordance with European distance selling regulations, customers have 14 days from receipt of their order to exercise their right of withdrawal, without needing to provide a reason."
                )}
              </p>
              <p className="mb-3">
                {t(
                  "Pour exercer ce droit, le client doit en informer Clips'ON par email à info@clips-on.ch en indiquant son numéro de commande. Les produits doivent être retournés dans leur emballage d'origine, non utilisés et en parfait état.",
                  "To exercise this right, the customer must notify Clips'ON by email at info@clips-on.ch, stating their order number. Products must be returned in their original packaging, unused and in perfect condition."
                )}
              </p>
              <p>
                {t(
                  "Les frais de retour sont à la charge du client. Le remboursement sera effectué dans les 14 jours suivant la réception du retour, via le même moyen de paiement que celui utilisé lors de la commande.",
                  "Return shipping costs are borne by the customer. Refunds will be issued within 14 days of receiving the return, using the same payment method as the original order."
                )}
              </p>
            </div>
          </section>

          {/* Article 8 — Garantie et responsabilité */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 8 — Garantie et responsabilité", "Article 8 — Warranty and liability")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Les produits Clips'ON sont conformes à la réglementation européenne en matière de sécurité des produits. En cas de produit défectueux, le client peut demander un échange ou un remboursement en contactant info@clips-on.ch dans les 30 jours suivant la réception.",
                  "Clips'ON products comply with European product safety regulations. In the event of a defective product, the customer may request an exchange or refund by contacting info@clips-on.ch within 30 days of receipt."
                )}
              </p>
              <p>
                {t(
                  "Clips'ON ne saurait être tenu responsable des dommages résultant d'une utilisation non conforme du produit.",
                  "Clips'ON shall not be held liable for damages resulting from improper use of the product."
                )}
              </p>
            </div>
          </section>

          {/* Article 9 — Données personnelles */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 9 — Données personnelles", "Article 9 — Personal data")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p>
                {t(
                  "Les données collectées lors de la commande sont nécessaires au traitement de celle-ci. Elles sont traitées conformément à notre ",
                  "Data collected during the ordering process is necessary for order fulfilment. It is processed in accordance with our "
                )}
                <Link 
                  to="/privacy-policy"
                  className="underline transition-colors"
                  style={{ color: '#E1593F' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                >
                  {t("Politique de confidentialité", "Privacy Policy")}
                </Link>
                .
              </p>
            </div>
          </section>

          {/* Article 10 — Droit applicable et litiges */}
          <section>
            <h2 
              className="mb-4 tracking-[0.2em] uppercase"
              style={{ 
                color: '#0D0C0B',
                fontSize: '20px',
                fontWeight: 300,
                letterSpacing: '4px'
              }}
            >
              {t("Article 10 — Droit applicable et litiges", "Article 10 — Applicable law and disputes")}
            </h2>
            <div style={{ color: '#82391F', fontSize: '17px', lineHeight: '1.8' }}>
              <p className="mb-3">
                {t(
                  "Les présentes CGV sont soumises au droit suisse. En cas de litige, les parties s'efforceront de trouver une solution amiable. À défaut, les tribunaux compétents de Genève seront seuls compétents.",
                  "These terms are governed by Swiss law. In the event of a dispute, the parties shall endeavour to reach an amicable solution. Failing this, the courts of Geneva shall have exclusive jurisdiction."
                )}
              </p>
              <p>
                {t(
                  "Pour les consommateurs résidant dans l'Union européenne, la plateforme de règlement en ligne des litiges de la Commission européenne est accessible à l'adresse : ",
                  "For consumers residing in the European Union, the European Commission's online dispute resolution platform is available at: "
                )}
                <a 
                  href="https://ec.europa.eu/consumers/odr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline transition-colors"
                  style={{ color: '#E1593F' }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#C4462E')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#E1593F')}
                >
                  https://ec.europa.eu/consumers/odr
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
